import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { withCookie, withURL } from '@lucid/utils';
import { useNavMenu, useNavButtons, useSiteMetadata, useSmartlingStrings } from '@lucid/hooks';

import { type ComponentImage } from '../../types/generics';
import { NavSection, type NavSectionOptions } from '../../modules/nav-section/nav-section';
import ABTest from '../ab-test/ab-test';
import LayoutContext from '../context/layout-context';
import type { Lucid } from '../../types/types';

export interface NavbarOptionalProps extends NavSectionOptions {
  /** The Directus Node ID */
  nid?: string;
  /** The page type. */
  pageType?: string;
  /** TODO FY26_LucidCoTryNowNavLink: remove shouldSkipTryNow */
  /** Checks if we should skip the try now page*/
  shouldSkipTryNow?: boolean;
}

export interface NavbarProps extends NavbarOptionalProps {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** Checks if we should hide the signup button. */
  shouldHideSignUpButton?: boolean;
  /** The translations to display in the LanguageSwitcher */
  nodeTranslations?: Lucid.Directus.Custom.NodeTranslation[];
  /** The main_nav id for the global nav */
  globalMenuId?: number;
  /** The main_nav id for the site nav */
  siteMenuId: number;
  /** The default page urls for the current site */
  defaultPageUrls?: Record<string, string>;
}

const Navbar: FC<PropsWithChildren<NavbarProps>> = ({
  buttonAriaLabel,
  nodeTranslations = [],
  shouldHideSignUpButton,
  pageType,
  hasGlobalNav,
  isGlobalNav = false,
  siteMenuId,
  globalMenuId,
  defaultPageUrls,
  /** TODO FY26_LucidCoTryNowNavLink: remove shouldSkipTryNow */
  shouldSkipTryNow = false,
  nid,
  ...rest
}) => {
  const { shortCode, product, urlLangPrefix } = useContext(LayoutContext);

  const { product: siteProduct } = useSiteMetadata();

  const siteNavMenu = useNavMenu(siteMenuId, shortCode);
  const globalNavMenu = useNavMenu(globalMenuId, shortCode);

  const withSmartling = useSmartlingStrings({
    keys: ['mktg_product_grid_aria_label'],
    shortCode,
  });
  // TODO: LucidSuiteNavWatchADemo2 - remove
  const { branch: abTestBranch } = useContext(ABTest.Context);
  const shouldUseDemoVideoLink =
    // TODO: LucidSuiteNavWatchADemo2 - remove abTestBranch condition
    abTestBranch === 'T_B' ||
    ['ppc-search-lander'].includes(pageType || '') ||
    (pageType === 'chart-homepage-2' && nid === '2');

  const isLoggedIn = !!withCookie.get('username');
  const navButtonParams = {
    isLoggedIn,
    shouldHideSignUpButton,
    languageKey: shortCode,
    product,
    defaultPageUrls,
    shouldUseDemoVideoLink,
  };
  /** TODO FY26_LucidCoTryNowNavLink: remove shouldSkipTryNow */
  const siteNavButtons = useNavButtons({ ...navButtonParams, shouldSkipTryNow });
  /** TODO FY26_LucidCoTryNowNavLink: remove shouldSkipTryNow */
  const globalNavButtons = useNavButtons({
    ...navButtonParams,
    isGlobalNav: true,
    shouldSkipTryNow,
  });

  const logos: Partial<Record<Lucid.Site.Product, ComponentImage>> = {
    chart: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidchart.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucidchart' }) || 'Go to homepage',
    },
    co: {
      imageSrc: 'https://cdn-cashy-static-assets.lucid.co/lucid/logos/lucid.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucid' }) || 'Go to homepage',
    },
    edu: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucid-for-education.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucid for Education' }) || 'Go to homepage',
    },
    scale: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidscale.com/lucid/logos/lucidscale.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucidscale' }) || 'Go to homepage',
    },
    spark: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidspark.com/lucid/logos/lucidspark.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucidspark' }) || 'Go to homepage',
    },
  };

  const nestedProducts = {
    chart: 'lucidchart',
    spark: 'lucidspark',
    scale: 'lucidscale',
  };
  const isNestedProduct = product in nestedProducts && product !== siteProduct;
  const nestedProduct = isNestedProduct ? nestedProducts[product] : undefined;
  const pathPrefix = nestedProduct || '';

  // TODO: LucidSuiteNavWatchADemo2 - remove
  const abTestContactSalesLink = {
    id: 'ab-test-nav-link',
    languages_code: {
      short_code: 'en',
    },
    is_dropdown: false,
    link: {
      id: 'ab-test-nav-link',
      default_link_text: 'Contact sales',
      final_url: '/contact/contact-sales',
    },
  } as Lucid.Directus.Generated.Nav_Section_Translations;

  return (
    <NavSection
      {...rest}
      buttonAriaLabel={buttonAriaLabel}
      globalNav={{
        buttons: globalNavButtons,
        // TODO: LucidSuiteNavWatchADemo2 - restore to globalNavMenu
        items: abTestBranch === 'T_B' ? [...(globalNavMenu || []), abTestContactSalesLink] : globalNavMenu,
      }}
      hasGlobalNav={hasGlobalNav}
      isGlobalNav={isGlobalNav}
      nodeTranslations={nodeTranslations}
      siteNav={{
        buttons: siteNavButtons,
        items: siteNavMenu,
        logo: logos[product ?? 'co'],
        logoLink: withURL('/', {
          urlLangPrefix,
          product,
          pageType,
          prefix: pathPrefix,
          willExcludePrefix: isGlobalNav,
        }),
      }}
    />
  );
};

export default Navbar;
